import type { WheelPrize, WheelTone } from "../../../../data";

interface PrizeWheelProps {
  prizes: WheelPrize[];
  // Current turn of the wheel, in degrees.
  rotation: number;
  ariaLabel: string;
  centerLabel: string;
}

// Slice fill and label colour for each tone - dark slices get white labels.
const toneClass: Record<WheelTone, { slice: string; label: string }> = {
  primary: { slice: "fill-primary", label: "fill-white" },
  peach: { slice: "fill-tone-peach", label: "fill-ink" },
  lilac: { slice: "fill-tone-lilac-ink", label: "fill-white" },
  sun: { slice: "fill-tone-sun", label: "fill-ink" },
  mint: { slice: "fill-tone-mint-ink", label: "fill-white" },
  sky: { slice: "fill-tone-sky", label: "fill-ink" },
};

const RADIUS = 150;
const LABEL_RADIUS = 92;

const pointAt = (degrees: number) => {
  const radians = (degrees * Math.PI) / 180;
  return `${(RADIUS * Math.cos(radians)).toFixed(1)} ${(RADIUS * Math.sin(radians)).toFixed(1)}`;
};

// The SVG prize wheel. Slice 0 starts at the top and slices run clockwise;
// the pointer sits above the wheel. Spinning is a CSS transition on the
// slice group, switched off for reduced motion.
const PrizeWheel = ({
  prizes,
  rotation,
  ariaLabel,
  centerLabel,
}: PrizeWheelProps) => {
  const slice = 360 / prizes.length;
  return (
    <div className="relative mx-auto w-full max-w-[360px]">
      <span
        aria-hidden="true"
        className="absolute -top-1.5 left-1/2 z-[2] size-0 -translate-x-1/2 border-x-[14px] border-t-[28px] border-x-transparent border-t-ink drop-shadow-md"
      />
      <svg
        viewBox="-160 -160 320 320"
        role="img"
        aria-label={ariaLabel}
        className="block h-auto w-full drop-shadow-xl"
      >
        <g
          style={{ transform: `rotate(${rotation}deg)` }}
          className="origin-[0_0] [transform-box:view-box] motion-safe:transition-transform motion-safe:duration-[4000ms] motion-safe:ease-[cubic-bezier(.12,.7,.1,1)]"
        >
          {prizes.map((prize, index) => {
            const start = -90 + index * slice;
            const middle = start + slice / 2;
            return (
              <g key={prize.label}>
                <path
                  d={`M0 0 L${pointAt(start)} A${RADIUS} ${RADIUS} 0 0 1 ${pointAt(start + slice)}Z`}
                  strokeWidth={3}
                  className={`stroke-white ${toneClass[prize.tone].slice}`}
                />
                <text
                  transform={`rotate(${middle}) translate(${LABEL_RADIUS} 0)${middle > 90 ? " rotate(180)" : ""}`}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={14}
                  fontWeight={700}
                  className={toneClass[prize.tone].label}
                >
                  {prize.label}
                </text>
              </g>
            );
          })}
        </g>
        <circle r={24} strokeWidth={3} className="fill-white stroke-line" />
        <text
          y={5}
          textAnchor="middle"
          fontSize={12}
          fontWeight={800}
          className="fill-primary"
        >
          {centerLabel}
        </text>
      </svg>
    </div>
  );
};

export default PrizeWheel;
